import type { CSSProperties, ReactNode } from 'react';

// A colored stat tile for dashboard widgets (e.g. a time-off balance).
// All tints are derived at runtime from a single `color` (a category hex),
// so the tile works for any admin-defined color without a hardcoded palette.
type BalanceTileProps = {
  /** Category / metric name shown above the value. */
  label: string;
  /** The headline value, e.g. "22.0" or "Unlimited". */
  value: string;
  /** Small unit after the value, e.g. "days". Omit for non-numeric values. */
  unit?: string;
  /** Muted caption under the value, e.g. "6 days scheduled". */
  subtitle?: string;
  /** Base hex color for the tile tint (background, border, icon, label). */
  color: string;
  /** Icon node, rendered inside a tinted chip. Inherits the tile color. */
  icon: ReactNode;
};

// color-mix keeps the hue and shifts lightness, so light and dark category
// colors both yield a legible tile. oklab mixing avoids muddy midpoints.
// Each mix resolves per scheme via CSS light-dark() (color-scheme is stamped on
// :root/:root.dark) — inline styles bypass the :root.dark variable re-pointing,
// so without this the tile stays a light pastel in dark mode while its
// slate-900 value text flips to near-white and vanishes on it. Same pattern as
// Avatar's soft variant: no reach into the theme store. Dark tints mix toward
// the surface token so the fill sits on the card; ink mixes toward white.
const tint = (color: string, lightPct: number, darkPct: number) =>
  `light-dark(color-mix(in oklab, ${color} ${lightPct}%, white), color-mix(in oklab, ${color} ${darkPct}%, var(--color-surface)))`;
const ink = (color: string) =>
  `light-dark(color-mix(in oklab, ${color} 72%, black), color-mix(in oklab, ${color} 55%, white))`;

export function BalanceTile({ label, value, unit, subtitle, color, icon }: BalanceTileProps) {
  // Borderless tinted tile per the design system: soft category-tinted fill, no border.
  const style: CSSProperties = {
    backgroundColor: tint(color, 10, 16),
  };
  const labelColor = ink(color);
  const chipStyle: CSSProperties = {
    backgroundColor: tint(color, 20, 30),
    color: labelColor,
  };

  return (
    <div data-testid="balance-tile" className="flex min-w-0 flex-col gap-2.5 overflow-hidden rounded-xl px-3.5 py-3" style={style}>
      <div className="flex items-center gap-2">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md" style={chipStyle}>
          {icon}
        </span>
        <span className="truncate text-[13px] font-semibold" style={{ color: labelColor }}>
          {label}
        </span>
      </div>
      <div className="flex min-w-0 items-baseline gap-1">
        <span className="truncate text-[28px] font-bold leading-none text-slate-900">{value}</span>
        {unit ? <span className="shrink-0 text-xs font-medium text-slate-500">{unit}</span> : null}
      </div>
      <p className="min-h-4 text-[11px] text-slate-500">{subtitle}</p>
    </div>
  );
}
